import { OrganizationManager, PartnerOrganization as PartnerOrganizationEntity, type PartnerOrganization } from "@entities";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { organizationManagerRepo } from "@repositories";
import type { HyperTableFilter } from "@shared/frontend/hyper-table";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";

import { normalizePhoneDigitsForSearch } from "@shared/helpers/normalize-phone-for-search";
import {
  PARTNER_ORG_HANDLED_BY_OPTIONS,
  type PartnerOrgHandledBy,
} from "@shared/helpers/partner-organization-handled-by";
import { COOPERATION_LEVEL_OPTIONS } from "./cooperation-level";

function toValidDate(value: unknown): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return Number.isFinite(date.getTime()) ? date : null;
}

function formatFilterDate(date: Date) {
  return dayjs(date).format("DD/MM/YYYY");
}

/**
 * מתנדב מאושר נחשב כהתקבל.
 * מאצ' של מנהל לפי matchedAt. הרשמה עצמית שמאושרת בלי matchedAt לפי createdAt.
 * מופע של קבוצת מועדים יורש את הארגון מהאב, אז סופרים גם משתתפים על הילדים.
 */
function approvedParticipantInRange(receivedAt: {
  $gte?: Date;
  $lte?: Date;
}) {
  return {
    status: "APPROVED" as const,
    $or: [
      { matchedAt: receivedAt },
      {
        matchedAt: null as unknown as Date,
        createdAt: receivedAt,
      },
    ],
  };
}

function noVolunteerReceivedWhere(start: Date | null, end: Date | null) {
  if (!start && !end) return {};
  const receivedAt = {
    ...(start ? { $gte: start } : {}),
    ...(end ? { $lte: end } : {}),
  };
  return PartnerOrganizationEntity.hyperFilter({
    opportunities: {
      $none: {
        $or: [
          {
            participantUsers: {
              $some: approvedParticipantInRange(receivedAt),
            },
          },
          {
            childOpportunities: {
              $some: {
                participantUsers: {
                  $some: approvedParticipantInRange(receivedAt),
                },
              },
            },
          },
        ],
      },
    },
  });
}

const noVolunteerReceivedFilter: HyperTableFilter<PartnerOrganization> = {
  id: "noVolunteerReceived",
  icon: "date",
  caption: "לא קיבלו מתנדב",
  renderFilter: ({ onSave, meta }) => {
    const startDate = toValidDate(meta.startDate);
    const endDate = toValidDate(meta.endDate);

    const saveRange = (start: Date | null, end: Date | null) => {
      const label =
        start && end
          ? `${formatFilterDate(start)} - ${formatFilterDate(end)}`
          : start
            ? `מ-${formatFilterDate(start)}`
            : end
              ? `עד ${formatFilterDate(end)}`
              : "";
      onSave({
        value: noVolunteerReceivedWhere(start, end),
        meta: { startDate: start, endDate: end, text: label },
      });
    };

    return (
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={
          heIL.components.MuiLocalizationProvider.defaultProps.localeText
        }
        adapterLocale="he"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            paddingInline: "0.5rem",
            minWidth: 280,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            ארגונים בלי מתנדב מאושר בטווח, כולל קבוצה וכולל מופע שירש את הארגון מהאב.
          </Typography>
          <DatePicker
            value={startDate ? dayjs(startDate) : null}
            onChange={(date) => {
              saveRange(
                date ? dayjs(date).startOf("day").toDate() : null,
                endDate,
              );
            }}
            label="מתאריך"
            reduceAnimations
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            value={endDate ? dayjs(endDate) : null}
            onChange={(date) => {
              saveRange(
                startDate,
                date ? dayjs(date).endOf("day").toDate() : null,
              );
            }}
            label="עד תאריך"
            reduceAnimations
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: "small" } }}
          />
        </Box>
      </LocalizationProvider>
    );
  },
};

export const PARTNER_ORGANIZATION_TABLE_FILTERS: HyperTableFilter<PartnerOrganization>[] =
  [
    { fieldName: "name", id: "name", caption: "שם" },
    {
      id: "dailyVolunteerTarget",
      icon: "literal",
      caption: "יעד יומי",
      renderFilter: ({ onSave, meta }) => {
        const state = (meta.state as "has" | "reached" | "open" | "") ?? "";
        const options = [
          { value: "has", label: "יש יעד" },
          { value: "reached", label: "הגיע ליעד" },
          { value: "open", label: "לא הגיע" },
        ] as const;
        return (
          <Select
            size="small"
            displayEmpty
            value={state}
            onChange={(event) => {
              const next = event.target.value as "has" | "reached" | "open" | "";
              const label =
                options.find((option) => option.value === next)?.label ?? "";
              onSave({
                value: next
                  ? PartnerOrganizationEntity.dailyVolunteerTargetState({
                      state: next,
                    })
                  : {},
                meta: { state: next, text: label },
              });
            }}
          >
            <MenuItem value="">הכל</MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    { fieldName: "description", id: "description", caption: "תיאור" },
    {
      id: "address",
      icon: "literal",
      caption: "כתובת",
      renderFilter: ({ onSave, meta }) => {
        const text = (meta.text as string) ?? "";
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
              minWidth: 220,
            }}
          >
            <TextField
              size="small"
              label="כתובת"
              defaultValue={text}
              onBlur={(event) => {
                const query = event.target.value.trim();
                onSave({
                  value: query
                    ? PartnerOrganizationEntity.hyperFilter({
                        $or: [
                          { address: { $contains: query } },
                          {
                            addresses: {
                              $some: { address: { $contains: query } },
                            },
                          },
                        ],
                      })
                    : {},
                  meta: { text: query },
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      id: "maxGroupSize",
      fieldName: "maxGroupSize",
      caption: "מקס אנשים בקבוצה",
    },
    {
      fieldName: "operatingHours",
      id: "operatingHours",
      caption: "שעות פעילות",
    },
    {
      id: "isAccessible",
      fieldName: "isAccessible",
      caption: "נגישות",
    },
    {
      id: "cooperationLevel",
      icon: "literal",
      caption: "רצינות",
      renderFilter: ({ onSave, meta }) => {
        const selected = (meta.selectedLevels as number[]) ?? [];
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
              minWidth: 240,
            }}
          >
            <Select
              multiple
              fullWidth
              size="small"
              value={selected}
              onChange={(e) => {
                const next = e.target.value as number[];
                onSave({
                  value: next.length
                    ? PartnerOrganizationEntity.hyperFilter({
                        cooperationLevel: { $in: next },
                      })
                    : {},
                  meta: {
                    selectedLevels: next,
                    text: next
                      .map(
                        (level) =>
                          COOPERATION_LEVEL_OPTIONS.find(
                            (option) => option.value === level,
                          )?.label ?? String(level),
                      )
                      .join(", "),
                  },
                });
              }}
              renderValue={(value) =>
                (value as number[]).length
                  ? (value as number[])
                      .map(
                        (level) =>
                          COOPERATION_LEVEL_OPTIONS.find(
                            (option) => option.value === level,
                          )?.label ?? String(level),
                      )
                      .join(", ")
                  : "בחר דירוג"
              }
            >
              {COOPERATION_LEVEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={selected.includes(option.value)} />
                  <ListItemText
                    primary={option.label}
                    secondary={`${option.band} · ${option.hint}`}
                  />
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      },
    },
    {
      id: "handledBy",
      icon: "literal",
      caption: "מטפל",
      renderFilter: ({ onSave, meta }) => {
        const selected = (meta.selectedHandlers as string[]) ?? [];
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
              minWidth: 220,
            }}
          >
            <Select
              multiple
              fullWidth
              size="small"
              value={selected}
              onChange={(e) => {
                const next = e.target.value as PartnerOrgHandledBy[];
                onSave({
                  value: next.length
                    ? PartnerOrganizationEntity.hyperFilter({
                        handledBy: { $in: next },
                      })
                    : {},
                  meta: {
                    selectedHandlers: next,
                    text: next
                      .map(
                        (value) =>
                          PARTNER_ORG_HANDLED_BY_OPTIONS.find(
                            (option) => option.value === value,
                          )?.label ?? value,
                      )
                      .join(", "),
                  },
                });
              }}
              renderValue={(value) =>
                (value as string[]).length
                  ? (value as string[])
                      .map(
                        (item) =>
                          PARTNER_ORG_HANDLED_BY_OPTIONS.find(
                            (option) => option.value === item,
                          )?.label ?? item,
                      )
                      .join(", ")
                  : "בחר מטפל"
              }
            >
              {PARTNER_ORG_HANDLED_BY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={selected.includes(option.value)} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      },
    },
    {
      id: "acceptsGroups",
      fieldName: "acceptsGroups",
      caption: "קולט קבוצות",
    },
    {
      id: "acceptsEnglishSpeakers",
      fieldName: "acceptsEnglishSpeakers",
      caption: "דוברי אנגלית",
    },
    {
      id: "organizationManager",
      icon: "literal",
      caption: "מנהל אחראי",
      renderFilter: ({ onSave, meta }) => {
        const [selectedManagers, setSelectedManagers] = useState<
          OrganizationManager[]
        >((meta.selectedManagers as OrganizationManager[]) ?? []);
        const [options, setOptions] = useState<OrganizationManager[]>([]);
        const [search, setSearch] = useState("");

        const searchManagers = useCallback(async (query: string) => {
          const q = query.trim();
          if (q.length < 2) {
            setOptions([]);
            return;
          }
          const phoneQuery = normalizePhoneDigitsForSearch(q);
          const list = await organizationManagerRepo.find({
            where: OrganizationManager.hyperFilter({
              $or: [
                { name: { $contains: q } },
                { phoneNumber: { $contains: phoneQuery } },
              ],
            }),
            include: { organization: true },
            orderBy: { name: "asc" },
            limit: 25,
          });
          setOptions(list);
        }, []);

        useEffect(() => {
          const handle = window.setTimeout(() => {
            void searchManagers(search);
          }, 250);
          return () => window.clearTimeout(handle);
        }, [search, searchManagers]);

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
              minWidth: 280,
            }}
          >
            <Autocomplete
              multiple
              options={options}
              value={selectedManagers}
              onChange={(_, value) => setSelectedManagers(value)}
              inputValue={search}
              onInputChange={(_, v) => setSearch(v)}
              getOptionLabel={(m) =>
                `${m.name}${m.organization?.name ? ` · ${m.organization.name}` : ""}`
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="הקלד לפחות 2 תווים"
              renderTags={(value, getTagProps) =>
                value.map((m, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={m.id}
                    label={m.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="חיפוש מנהל אחראי"
                  size="small"
                />
              )}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  const managerIds = selectedManagers.map((m) => m.id);
                  onSave({
                    value: managerIds.length
                      ? PartnerOrganizationEntity.hyperFilter({
                          handlingManagerId: { $in: managerIds },
                        })
                      : {},
                    meta: {
                      selectedManagers,
                      text: selectedManagers.map((m) => m.name).join(", "),
                    },
                  });
                }}
              >
                שמירה
              </Button>
            </Box>
          </Box>
        );
      },
    },
    noVolunteerReceivedFilter,
    {
      id: "hasOpenOpportunities",
      icon: "boolean",
      caption: "יש משימות פתוחות",
      renderFilter: ({ onSave, meta }) => {
        const value = (meta.value as string) ?? "";
        return (
          <Box sx={{ p: 1, minWidth: 200 }}>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={value}
              onChange={(event) => {
                const next = event.target.value;
                const open = {
                  opportunities: {
                    $some: { isDeleted: false, isClosed: false },
                  },
                };
                const closed = {
                  opportunities: {
                    $none: { isDeleted: false, isClosed: false },
                  },
                };
                onSave({
                  value:
                    next === "yes"
                      ? PartnerOrganizationEntity.hyperFilter(open)
                      : next === "no"
                        ? PartnerOrganizationEntity.hyperFilter(closed)
                        : {},
                  meta: {
                    value: next,
                    text: next === "yes" ? "יש משימות פתוחות" : next === "no" ? "אין משימות פתוחות" : "",
                  },
                });
              }}
            >
              <MenuItem value="">הכל</MenuItem>
              <MenuItem value="yes">יש משימות פתוחות</MenuItem>
              <MenuItem value="no">אין משימות פתוחות</MenuItem>
            </Select>
          </Box>
        );
      },
    },
    {
      id: "hasCommunication",
      icon: "boolean",
      caption: "תיעוד תקשורת",
      renderFilter: ({ onSave, meta }) => {
        const value = (meta.value as string) ?? "";
        return (
          <Box sx={{ p: 1, minWidth: 180 }}>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={value}
              onChange={(event) => {
                const next = event.target.value;
                onSave({
                  value:
                    next === "yes"
                      ? PartnerOrganizationEntity.hyperFilter({
                          communicationRecords: { $some: {} },
                        })
                      : next === "no"
                        ? PartnerOrganizationEntity.hyperFilter({
                            communicationRecords: { $none: {} },
                          })
                        : {},
                  meta: {
                    value: next,
                    text: next === "yes" ? "יש תיעוד" : next === "no" ? "אין תיעוד" : "",
                  },
                });
              }}
            >
              <MenuItem value="">הכל</MenuItem>
              <MenuItem value="yes">יש תיעוד</MenuItem>
              <MenuItem value="no">אין תיעוד</MenuItem>
            </Select>
          </Box>
        );
      },
    },
    {
      id: "communicationInRange",
      icon: "date",
      caption: "תיעוד תקשורת בטווח",
      renderFilter: ({ onSave, meta }) => {
        const startDate = toValidDate(meta.startDate);
        const endDate = toValidDate(meta.endDate);
        const saveRange = (start: Date | null, end: Date | null) => {
          onSave({
            value:
              start || end
                ? PartnerOrganizationEntity.hyperFilter({
                    communicationRecords: {
                      $some: {
                        createdAt: {
                          ...(start ? { $gte: dayjs(start).startOf("day").toDate() } : {}),
                          ...(end ? { $lte: dayjs(end).endOf("day").toDate() } : {}),
                        },
                      },
                    },
                  })
                : {},
            meta: {
              startDate: start,
              endDate: end,
              text:
                start || end
                  ? [start ? formatFilterDate(start) : "", end ? formatFilterDate(end) : ""]
                      .filter(Boolean)
                      .join(" - ")
                  : "",
            },
          });
        };
        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
            adapterLocale="he"
          >
            <Box sx={{ display: "flex", gap: 1, p: 1, flexWrap: "wrap" }}>
              <DatePicker
                label="מתאריך"
                value={startDate ? dayjs(startDate) : null}
                format="DD/MM/YYYY"
                onChange={(date) => saveRange(date ? dayjs(date).toDate() : null, endDate)}
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="עד תאריך"
                value={endDate ? dayjs(endDate) : null}
                format="DD/MM/YYYY"
                onChange={(date) => saveRange(startDate, date ? dayjs(date).toDate() : null)}
                slotProps={{ textField: { size: "small" } }}
              />
            </Box>
          </LocalizationProvider>
        );
      },
    },
  ];
